import { Suspense, lazy, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import * as Sentry from "@sentry/react";

import App from "../app/index";
import RequireAuth from "../components/RequireAuth";
import { Locations } from "../interfaces/enums";
import { RoutesPermissions } from "../components/RequireFlag";
import VerifyEmail from "../components/VerifyEmail";
import { CcyConversionProvider } from "../contexts/CcyConversionContext";

import Login from "../pages/login";
// import SignUp from "../pages/signUp";
import ResetPassword from "../pages/resetPassword";
import { ErrorPage } from "@/pages/error";

const OnboardingsPage = lazy(
  () => import("../pages/sendOnboarding/OnboardingsPage"),
);

const OrdersCcyConversion = lazy(
  () => import("../pages/ordersCcyConversion/OrdersPage"),
);

const GetQuotePage = lazy(
  () => import("../pages/orderCCyConversion/Router/PlaceOrder"),
);
const OrderPage = lazy(
  () => import("../pages/orderCCyConversion/Router/OrderPage"),
);
const DestinationBankAccountsRouter = lazy(
  () =>
    import("../pages/destinationBankAccountsCcyConversion/Router/DestinationAccountsPage"),
);
const AnalyticsPage = lazy(
  () => import("../pages/admin/analytics/AnalyticsPage"),
);
const AdminOnboarding = lazy(
  () => import("../pages/admin/onboardings/AdminOnboardingsPage"),
);
const AdminSummary = lazy(
  () => import("../pages/admin/orders/AdminOrdersPage"),
);
const AdminDFXOrders = lazy(
  () => import("../pages/admin/dfxorders/AdminDFXOrdersPage"),
);
const ManualOrder = lazy(() => import("../pages/admin/ManualOrder"));
const UserAdminClients = lazy(
  () => import("../pages/usersCreation/UsersAdminPage"),
);
const TransfersAdmin = lazy(
  () => import("../pages/admin/transfers/TransfersPage"),
);
const AccountsAdmin = lazy(
  () => import("../pages/admin/accounts/AdminDestinationAccountsPage"),
);
const FeVersionAdmin = lazy(
  () => import("../pages/admin/fe_version/FeVersionPage"),
);
const QuoteStreamAdmin = lazy(
  () => import("../pages/admin/quote_stream/QuoteStreamPage"),
);
const CompliancePage = lazy(
  () => import("../pages/admin/compliance/CompliancePage"),
);
const TermsAndConditions = lazy(
  () => import("../pages/documentation/terms-and-conditions"),
);
const ApiManagement = lazy(() => import("../pages/api/ApiManagementPage"));
const ApiDocsPage = lazy(() => import("../pages/apiDocs/ApiDocsPage"));
const CockpitPage = lazy(() => import("../pages/cockpit/CockpitPage"));
const LeadsPage = lazy(() => import("../pages/leads/LeadsPage"));

const AppRouteDetector = (props: any) => {
  const location = useLocation();

  useEffect(() => {
    // Scroll to top on every route change
    window.scrollTo(0, 0);
  }, [location]);

  return <Routes key={location.pathname}>{props.children}</Routes>;
};

const SuspenseElement = () => {
  return null;
};

const AppRoutes = () => (
  <BrowserRouter>
    <AppRouteDetector>
      {/* Private Routes */}
      <Route
        path={Locations["/"]}
        element={
          <Sentry.ErrorBoundary fallback={<ErrorPage />}>
            <RequireAuth redirectTo={Locations["/login"]}>
              <App />
            </RequireAuth>
          </Sentry.ErrorBoundary>
        }
      >
        <Route path={Locations["/"]} element={<RoutesPermissions />}>
          <Route path="/ccy-conversion" element={<CcyConversionProvider />}>
            {/* Currency Conversion */}

            <Route
              path={Locations["/ccy-conversion/orders/:id"]}
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <OrderPage />
                </Suspense>
              }
            />

            <Route
              path={Locations["/ccy-conversion/place-order"]}
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <GetQuotePage />
                </Suspense>
              }
            />

            <Route
              path={Locations["/ccy-conversion/orders"]}
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
                    <OrdersCcyConversion />
                  </Sentry.ErrorBoundary>
                </Suspense>
              }
            />

            <Route
              path={
                Locations["/ccy-conversion/destination-bank-accounts-router"]
              }
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <DestinationBankAccountsRouter />
                </Suspense>
              }
            />
          </Route>

          {/* OTCs */}

          <Route path={Locations["/admin"]}>
            <Route
              path="onboardings"
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <AdminOnboarding />
                </Suspense>
              }
            />
            <Route
              path="orders"
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <AdminSummary />
                </Suspense>
              }
            />
            <Route
              path="analytics"
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <AnalyticsPage />
                </Suspense>
              }
            />
            <Route
              path="compliance"
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <CompliancePage />
                </Suspense>
              }
            />
            <Route
              path="users"
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <UserAdminClients />
                </Suspense>
              }
            />

            <Route
              path="transfers"
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <TransfersAdmin />
                </Suspense>
              }
            />
            <Route
              path="create-manual-order"
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <ManualOrder />
                </Suspense>
              }
            />
            <Route
              path="accounts"
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <AccountsAdmin />
                </Suspense>
              }
            />
            <Route
              path="fe-version"
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <FeVersionAdmin />
                </Suspense>
              }
            />
            <Route
              path="mexdolar-orders"
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <AdminDFXOrders />
                </Suspense>
              }
            />
            <Route
              path="quote-stream"
              element={
                <Suspense fallback={<SuspenseElement />}>
                  <QuoteStreamAdmin />
                </Suspense>
              }
            />
          </Route>

          <Route
            path={Locations["/send-onboarding"]}
            element={
              <Suspense fallback={<SuspenseElement />}>
                <OnboardingsPage />
              </Suspense>
            }
          />

          <Route
            path={Locations["/cockpit"]}
            element={
              <Suspense fallback={<SuspenseElement />}>
                <CockpitPage />
              </Suspense>
            }
          />

          <Route
            path={Locations["/leads"]}
            element={
              <Suspense fallback={<SuspenseElement />}>
                <LeadsPage />
              </Suspense>
            }
          />
        </Route>
        <Route
          path={Locations["/terms-and-conditions"]}
          element={
            <Suspense fallback={<SuspenseElement />}>
              <TermsAndConditions />
            </Suspense>
          }
        />
        <Route
          path={Locations["/api-management"]}
          element={
            <Suspense fallback={<SuspenseElement />}>
              <ApiManagement />
            </Suspense>
          }
        />
      </Route>
      {/* Public Routes */}
      <Route path={Locations["/login"]} element={<Login />} />
      {/* <Route path={Locations["/sign-up"]} element={<SignUp />} /> */}
      <Route path={Locations["/verify-email"]} element={<VerifyEmail />} />
      <Route path={Locations["/reset-password"]} element={<ResetPassword />} />
      <Route
        path={Locations["/api-docs"]}
        element={
          <Suspense fallback={<SuspenseElement />}>
            <ApiDocsPage />
          </Suspense>
        }
      />
      <Route
        path={Locations["/access-denied"]}
        element={<h1>Access denied</h1>}
      />
      <Route path={"*"} element={<Navigate to={Locations["/"]} />} />
    </AppRouteDetector>
  </BrowserRouter>
);

export default AppRoutes;
